import { Button, Tooltip } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import type { Grid, GridBlock, ApartmentStatus } from '../api/apartments';
import { formatUzsCompact } from '@/shared/lib/format';
import s from './apartment-grid.module.css';

type Props = {
  grid: Grid;
  onSelectUnit: (apartmentId: string) => void;
  onDeleteBlock?: (block: GridBlock) => void;
};

const STATUS_CLASS: Record<ApartmentStatus, string> = {
  available: s.statusAvailable!,
  reserved: s.statusReserved!,
  contracted: s.statusContracted!,
  sold: s.statusSold!,
  delivered: s.statusDelivered!,
  cancelled: s.statusCancelled!,
};

const STATUS_KEYS: ApartmentStatus[] = ['available', 'reserved', 'contracted', 'sold', 'delivered', 'cancelled'];

export function ApartmentGrid({ grid, onSelectUnit, onDeleteBlock }: Props) {
  const { t } = useTranslation();

  if (grid.blocks.length === 0) {
    return <div className={s.empty}>{t('apartments.empty')}</div>;
  }

  return (
    <div className={s.grid}>
      <div className={s.legend}>
        {STATUS_KEYS.map((status) => (
          <span key={status} className={s.legendItem}>
            <span className={`${s.swatch} ${STATUS_CLASS[status]}`} />
            {t(`apartments.status.${status}`)}
            {grid.counts[status] ? <strong> · {grid.counts[status]}</strong> : null}
          </span>
        ))}
      </div>

      <div className={s.blocksRow}>
        {grid.blocks.map((block) => (
          <div key={block.id} className={s.block}>
            <div
              className={s.blockHeader}
              style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}
            >
              <div>
                <div className={s.blockName}>{block.name}</div>
                <div className={s.blockMeta}>
                  {t('blocks.code')} {block.code} ·{' '}
                  {block.floors.reduce((sum, f) => sum + f.units.length, 0)} {t('apartments.units')}
                </div>
              </div>
              {onDeleteBlock && (
                <Button
                  type="text"
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => onDeleteBlock(block)}
                  aria-label={t('blocks.delete')}
                  title={t('blocks.delete')}
                />
              )}
            </div>
            <div className={s.floors}>
              {block.floors.map((floor) => (
                <div key={floor.id} className={s.floor}>
                  <div className={s.floorNum}>{floor.number}</div>
                  <div className={s.units}>
                    {floor.units.map((unit) => (
                      <Tooltip
                        key={unit.id}
                        title={
                          <div style={{ fontSize: 11, lineHeight: 1.5 }}>
                            <div>
                              <strong>{unit.fullCode}</strong>
                            </div>
                            <div>{t(`apartments.layout.${unit.layoutType}`)} · {Number(unit.areaSqm)} м²</div>
                            <div>{formatUzsCompact(unit.basePrice)}</div>
                            <div style={{ marginTop: 4, opacity: 0.7 }}>{t(`apartments.status.${unit.status}`)}</div>
                          </div>
                        }
                      >
                        <button
                          type="button"
                          className={`${s.unit} ${STATUS_CLASS[unit.status]}`}
                          onClick={() => onSelectUnit(unit.id)}
                          aria-label={`${unit.fullCode} — ${unit.status}`}
                        >
                          {unit.number}
                        </button>
                      </Tooltip>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
